import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';

// ── Type Definitions ──────────────────────────────────────────

interface User {
  id: string;
  fullName: string;
  email: string;
  role: string;
}

interface Event {
  id: string;
  name: string;
  date: string;
}

interface TicketTier {
  id: string;
  name: string;
  price: number | string;
  description: string;
  active: boolean;
}

interface MealOption {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface RegistrationResponse {
  id: string;
  userId: string;
  eventId: string;
  user: { fullName: string; email: string };
  event: { name: string };
}

interface ApiError {
  statusCode?: number;
  message?: string;
}

// ── Inline Styles ─────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    marginBottom: '1rem',
  },
  label: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  select: {
    padding: '0.5rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
    maxWidth: '400px',
    width: '100%',
  },
  input: {
    padding: '0.5rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
    maxWidth: '400px',
    width: '100%',
  },
  btn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnDisabled: {
    padding: '0.5rem 1rem',
    backgroundColor: '#999',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  success: {
    color: '#2e7d32',
    backgroundColor: '#e8f5e9',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  hint: {
    fontSize: '0.75rem',
    color: '#666',
    marginTop: '0.25rem',
  },
};

// ── Component ─────────────────────────────────────────────────

export function ManualRegistration() {
  const [users, setUsers] = useState<User[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [tiers, setTiers] = useState<TicketTier[]>([]);
  const [mealOptions, setMealOptions] = useState<MealOption[]>([]);

  const [ticketOrderId, setTicketOrderId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedTierId, setSelectedTierId] = useState('');
  const [selectedMealOptionId, setSelectedMealOptionId] = useState('');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Data Fetching ─────────────────────────────────────────────

  useEffect(() => {
    async function fetchData() {
      try {
        const [usersData, eventsData, tiersData, mealsData] = await Promise.all([
          apiClient<User[]>('/users'),
          apiClient<Event[]>('/events'),
          apiClient<TicketTier[]>('/legacy-ball/tiers'),
          apiClient<MealOption[]>('/legacy-ball/meal-options'),
        ]);
        setUsers(usersData);
        setEvents(eventsData);
        setTiers(tiersData);
        setMealOptions(mealsData.filter((m) => m.active));
      } catch (err: unknown) {
        const apiErr = err as ApiError;
        setError(apiErr.message || 'Failed to load data.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // ── Actions ───────────────────────────────────────────────────

  async function handleRegister() {
    if (!ticketOrderId.trim() || !selectedUserId || !selectedEventId || !selectedTierId) {
      setError('Please fill in all fields.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Step 1: Create registration for existing user
      const registration = await apiClient<RegistrationResponse>(
        '/registrations/admin',
        {
          method: 'POST',
          body: {
            userId: selectedUserId,
            eventId: selectedEventId,
          },
        },
      );

      // Step 2: Create ticket linked to the new registration
      await apiClient('/legacy-ball/tickets', {
        method: 'POST',
        body: {
          registrationId: registration.id,
          tierId: selectedTierId,
        },
      });

      // Step 3: Create meal selection if a meal was chosen
      if (selectedMealOptionId) {
        await apiClient('/legacy-ball/meal-selections', {
          method: 'POST',
          body: {
            registrationId: registration.id,
            mealOptionId: selectedMealOptionId,
          },
        });
      }

      setSuccess(
        `Registration & ticket created successfully. ` +
        `Registration ID: ${registration.id.slice(0, 8)}… | ` +
        `Ticket Order ID: ${ticketOrderId.trim()}`,
      );

      // Reset form
      setTicketOrderId('');
      setSelectedUserId('');
      setSelectedEventId('');
      setSelectedTierId('');
      setSelectedMealOptionId('');
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.statusCode === 409) {
        setError(apiErr.message || 'User is already registered for this event.');
      } else {
        setError(apiErr.message || 'Failed to create registration and ticket.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────

  const isFormValid =
    ticketOrderId.trim() && selectedUserId && selectedEventId && selectedTierId;

  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>Manual Registration</h2>

      {error && (
        <div style={styles.error} onClick={() => setError('')}>
          {error} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {success && (
        <div style={styles.success} onClick={() => setSuccess('')}>
          {success} <span style={{ float: 'right', fontWeight: 700 }}>×</span>
        </div>
      )}

      {loading ? (
        <p style={{ color: '#666' }}>Loading...</p>
      ) : (
        <>
          {/* Ticket Order ID */}
          <div style={styles.field}>
            <label style={styles.label}>Ticket Order ID</label>
            <input
              type="text"
              style={styles.input}
              value={ticketOrderId}
              onChange={(e) => setTicketOrderId(e.target.value)}
              placeholder="e.g. EVB-12345"
            />
            <span style={styles.hint}>
              External reference from ticketing system (e.g. Eventbrite)
            </span>
          </div>

          {/* User Selection */}
          <div style={styles.field}>
            <label style={styles.label}>User</label>
            <select
              style={styles.select}
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">— Choose a user —</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.fullName} ({user.email})
                </option>
              ))}
            </select>
          </div>

          {/* Event Selection */}
          <div style={styles.field}>
            <label style={styles.label}>Event</label>
            <select
              style={styles.select}
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              <option value="">— Choose an event —</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </div>

          {/* Tier Selection */}
          <div style={styles.field}>
            <label style={styles.label}>Ticket Tier</label>
            <select
              style={styles.select}
              value={selectedTierId}
              onChange={(e) => setSelectedTierId(e.target.value)}
            >
              <option value="">— Choose a tier —</option>
              {tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name} — ${Number(tier.price).toFixed(2)}
                </option>
              ))}
            </select>
          </div>

          {/* Meal Selection */}
          <div style={styles.field}>
            <label style={styles.label}>Meal Selection (optional)</label>
            <select
              style={styles.select}
              value={selectedMealOptionId}
              onChange={(e) => setSelectedMealOptionId(e.target.value)}
            >
              <option value="">— No meal selection —</option>
              {mealOptions.map((meal) => (
                <option key={meal.id} value={meal.id}>
                  {meal.name}{meal.description ? ` — ${meal.description}` : ''}
                </option>
              ))}
            </select>
            <span style={styles.hint}>
              Optional: assign a meal preference for the Legacy Ball
            </span>
          </div>

          {/* Submit Button */}
          <button
            style={!isFormValid || submitting ? styles.btnDisabled : styles.btn}
            onClick={handleRegister}
            disabled={!isFormValid || submitting}
          >
            {submitting ? 'Registering…' : 'Register & Create Ticket'}
          </button>
        </>
      )}
    </div>
  );
}
